import React from "react";
import { errText, t } from "./i18n";
import { leaveVoice, joinVoice, toggleMic, useVoice, type LinkState } from "./voice";
import { Avatar, Button, MicIcon, cx } from "./ui";

const LINK_LABEL: Record<LinkState, string> = {
  connecting: "voiceConnecting",
  connected: "voiceConnected",
  reconnecting: "voiceReconnecting",
  failed: "voiceFailed",
};

/** Group voice card for a room. Lives in the room's side column, so it never covers the game board. */
export function VoicePanel({ roomId, meId, hostId }: { roomId: string; meId: string; hostId: string }) {
  const v = useVoice();
  const inHere = v.roomId === roomId;
  const mine = v.participants.find((p) => p.userId === meId);
  const status = !inHere
    ? t.voiceHint
    : mine?.serverMuted
      ? t.voiceMutedByMod
      : v.micOn
        ? t.voiceMicOpen
        : t.voiceListening;

  return (
    <section className="voice-panel" aria-label={t.voiceTitle}>
      <header className="voice-head">
        <strong>{t.voiceTitle}</strong>
        {inHere && <span className="voice-count" dir="ltr">{v.participants.length}/{v.max}</span>}
      </header>
      <p className="small muted voice-status" role="status" aria-live="polite">{status}</p>
      {!inHere ? (
        <>
          <Button kind="ghost" className="voice-btn" onClick={() => void joinVoice(roomId, meId)}>
            {t.voiceJoin}
          </Button>
          {v.error && <p className="small voice-error" role="alert">{errText({ code: v.error })}</p>}
        </>
      ) : (
        <>
          <div className="voice-controls">
            <Button
              kind={v.micOn ? "primary" : "ghost"}
              className="voice-btn"
              onClick={() => void toggleMic()}
              disabled={!!mine?.serverMuted}
              aria-pressed={v.micOn}
            >
              {v.micOn ? t.voiceMicOff : t.voiceMicOn}
            </Button>
            <Button kind="ghost" className="voice-btn" onClick={leaveVoice}>{t.voiceLeave}</Button>
          </div>
          {v.error && (
            <p className="small voice-error" role="alert">
              {errText({ code: v.error })}
              {v.error === "mic_permission_denied" && <> — {t.voiceMicSettings}</>}
            </p>
          )}
          {v.participants.length === 0 ? (
            <p className="small muted">{t.voiceEmpty}</p>
          ) : (
            <ul className="voice-roster">
              {v.participants.map((p) => {
                const isMe = p.userId === meId;
                const link = isMe ? null : v.links[p.userId];
                const speaking = !!v.speaking[p.userId];
                const micLabel = p.serverMuted ? t.voiceMutedByMod : p.muted ? t.voiceMicClosed : t.voiceMicOpen;
                return (
                  <li key={p.userId} className={cx("voice-p", speaking && "is-speaking")}>
                    <Avatar name={p.username} url={p.avatar} size={30} />
                    <span className="voice-name">
                      <strong>{isMe ? t.you : p.username}</strong>
                      {p.userId === hostId && <span className="mod-badge">{t.host}</span>}
                    </span>
                    {speaking && <span className="voice-speaking" aria-label={t.voiceSpeaking}>●</span>}
                    {link && <span className={cx("voice-link", `link-${link}`)} title={t[LINK_LABEL[link] as "voiceConnected"]} aria-label={t[LINK_LABEL[link] as "voiceConnected"]} />}
                    <span
                      className={cx("voice-mic", p.serverMuted ? "is-server" : p.muted ? "is-off" : "is-on")}
                      title={micLabel}
                      aria-label={micLabel}
                    >
                      <MicIcon state={p.serverMuted ? "server" : p.muted ? "off" : "on"} />
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
